import type {
  Segment,
  SegmentAnalysisPortfolioPayload,
  SegmentAnalysisRunMode,
  SegmentAnalysisRunPayload,
  SegmentCreateDraft,
  SegmentPendingChange,
  SegmentPendingChangesPayload,
} from "../types"

import { api } from "@/lib/api-client"

/**
 * Opts a mutation into the shared client's bounded 401 refresh-and-replay
 * (#2904).
 *
 * Every write below used to be wrapped in a feature-local `withAuthRetry`
 * helper that did exactly this — refresh once, replay once, surface the
 * original 401 on failure — so this flag preserves behaviour rather than
 * introducing it. It is set per call site, not globally, because a 401 is not
 * proof that the backend handler ran no side effect; these particular endpoints
 * reject before their handler body, which is why they were audited as safe to
 * replay. Reads need no flag: GET/HEAD replay by default.
 */
const REPLAY_ON_EXPIRED_TOKEN = { authRetry: true } as const

export function fetchSegmentAnalysisPortfolio(): Promise<SegmentAnalysisPortfolioPayload> {
  return api.get<never, SegmentAnalysisPortfolioPayload>("/segment-analysis/portfolio")
}

export function startSegmentAnalysisRun(input: {
  mode: SegmentAnalysisRunMode
  segmentId: string
  /** Chat model catalog id from `/api/config` models (same catalog as chat/settings). */
  modelId?: string
  /** Reasoning effort override (same ladder as Explorer ReasoningModePill). */
  reasoningEffort?: string
}): Promise<SegmentAnalysisRunPayload> {
  return api.post<typeof input, SegmentAnalysisRunPayload>(
    "/segment-analysis/runs",
    input,
    REPLAY_ON_EXPIRED_TOKEN
  )
}

export function fetchSegmentAnalysisRun(runId: string): Promise<SegmentAnalysisRunPayload> {
  return api.get<never, SegmentAnalysisRunPayload>(`/segment-analysis/runs/${runId}`)
}

export function createSegmentAnalysisSegment(draft: SegmentCreateDraft): Promise<Segment> {
  return api.post<SegmentCreateDraft, Segment>(
    "/segment-analysis/segments",
    draft,
    REPLAY_ON_EXPIRED_TOKEN
  )
}

export function toggleSegmentAnalysisFavourite(
  segmentId: string
): Promise<{ segmentId: string; isFavourite: boolean }> {
  return api.post<Record<string, never>, { segmentId: string; isFavourite: boolean }>(
    `/segment-analysis/segments/${segmentId}/favourite`,
    {},
    REPLAY_ON_EXPIRED_TOKEN
  )
}

export function toggleSegmentAnalysisCompanyFavourite(
  segmentId: string,
  companyName: string
): Promise<{ segmentId: string; companyName: string; isFavourite: boolean }> {
  return api.post<
    { companyName: string },
    { segmentId: string; companyName: string; isFavourite: boolean }
  >(
    `/segment-analysis/segments/${segmentId}/companies/favourite`,
    { companyName },
    REPLAY_ON_EXPIRED_TOKEN
  )
}

/** Reclassify a company customer↔prospect within a segment (§5.2, edit-gated). */
export function reclassifySegmentAnalysisCompany(
  segmentId: string,
  input: { companyName: string; classification: "customer" | "prospect" }
): Promise<{
  segmentId: string
  companyName: string
  classification: "customer" | "prospect" | null
  outcome: "pending" | "applied" | "conflict" | "failed"
  change?: SegmentPendingChange
  remaining?: number
}> {
  return api.post(
    `/segment-analysis/segments/${segmentId}/companies/classification`,
    input,
    REPLAY_ON_EXPIRED_TOKEN
  )
}

export function fetchSegmentAnalysisPending(
  segmentId: string
): Promise<SegmentPendingChangesPayload> {
  return api.get<never, SegmentPendingChangesPayload>(
    `/segment-analysis/segments/${segmentId}/pending`
  )
}

export function decideSegmentAnalysisChanges(
  segmentId: string,
  input: { decision: "approve" | "reject"; changeId?: string }
): Promise<{ segmentId: string; approved: string[]; rejected: string[]; remaining: number }> {
  return api.post(
    `/segment-analysis/segments/${segmentId}/decision`,
    input,
    REPLAY_ON_EXPIRED_TOKEN
  )
}

/** Transfer a segment's ownership (admin only, §5.7). Redirects the approver of record. */
export function reassignSegmentAnalysisOwner(
  segmentId: string,
  input: { ownerOid?: string | null; ownerName: string }
): Promise<Segment> {
  return api.post<typeof input, Segment>(
    `/segment-analysis/segments/${segmentId}/owner`,
    input,
    REPLAY_ON_EXPIRED_TOKEN
  )
}

/** Persist manual sales / addressable-market estimates (segment owner only). */
export function patchSegmentAnalysisSegment(
  segmentId: string,
  input: { sales?: number; addressable?: Record<string, number> }
): Promise<Segment> {
  return api.patch<typeof input, Segment>(
    `/segment-analysis/segments/${segmentId}`,
    input,
    REPLAY_ON_EXPIRED_TOKEN
  )
}
